import { createElement, useCallback } from 'react';
import { SironaSupportLink } from 'common/SironaSupportLink';
import { useDebouncedCallback } from 'use-debounce';
import type { ApolloError } from '@apollo/client';
import { useMutation } from '@apollo/client';
import { useSetRecoilState } from 'recoil';
import { reporterSavingState, ReporterSaveState } from 'config/recoilState';
import type {
  UpdateReportMutation,
  UpdateReportMutationVariables,
  UpdateAddendumMutation,
  UpdateAddendumMutationVariables,
} from 'generated/graphql';
import { sanitizeAddendumContentForPersistence } from './persistenceSanitizers';
import { AUTO_SAVE_DEBOUNCE_MS, AUTO_SAVE_MAX_WAIT_MS } from './constants';
import {
  convertFieldsToUnified,
  convertUnifiedToFields,
  getReportSMID,
  getCaseInfo,
  enqueueAutoSaveErrorToast,
} from './utils';
import { getPicklistsNodesFromParentNode } from '../../RichTextEditor/plugins/picklist/utils';
import { Element, Node } from '../../RichTextEditor/core';
import { logger } from 'modules/logger';
import { ReportExitAction } from '../../RichTextEditor/analytics/performance';
import type { SlateContent } from '../types';
import type { LoadedReportWorklistItem } from 'hooks/useCurrentCaseReport';
import type { EnqueueToastFn } from 'common/ui/Toaster';
import type { Picklists, Macros } from '../../Template/usePicklist';
import type { HandleAutoloadNextCaseParams } from 'hooks/useWorklistAutoLoad';

export type UpdateType = 'contentChange' | 'templateApply';

const getDraftCaseInfo = ({ patientName, accessionNumber }: LoadedReportWorklistItem) =>
  `${patientName ?? 'Patient Name: N/A'} (${accessionNumber ?? 'Accession Number: N/A'})`;

const createDraftSaveErrorMessage = (message: string) =>
  createElement(
    'span',
    null,
    message,
    ' Try again.',
    ' If the problem persists, contact ',
    createElement(SironaSupportLink),
    '.'
  );

const getSaveErrorReason = (e: ApolloError) =>
  e.networkError ? 'a connection error' : 'an unexpected error';

export type FieldsUpdateProps = {
  contentToSubmit: SlateContent;
  updateType?: Nullable<UpdateType>;
  activateTemplateIDToSubmit?: string;
  isExplicitDraftSave?: boolean;
  workstationIDToSubmit?: string | null;
  errorMode?: 'default' | 'submissionWorkstationPatch';
  onSuccess?: () => void;
  onError?: (error: ApolloError) => void;
};

export interface UseAutoSaveReportParams {
  worklistItem: LoadedReportWorklistItem;
  isReportFacadeEnabled: boolean;
  updateReport: ReturnType<
    typeof useMutation<UpdateReportMutation, UpdateReportMutationVariables>
  >[0];
  updateAddendum: ReturnType<
    typeof useMutation<UpdateAddendumMutation, UpdateAddendumMutationVariables>
  >[0];
  isClaimedByMe: boolean;
  workstationID: Nullable<string>;
  picklists: Picklists;
  insertedMacros: Macros;
  enqueueToast: EnqueueToastFn;
  closeToast: (toastKey: string) => void;
  maybeAutoloadNextCaseCallback: (
    isWorklistAutoLoadEnabled: boolean,
    defaultToastMsg: string,
    params: HandleAutoloadNextCaseParams
  ) => Promise<unknown>;
  isWorklistAutoLoadEnabled: boolean;
  setDraftReportWorklistSmid: (value: string) => void;
  setHasReportContentChanged: (value: boolean) => void;
  setEditorBackup: (content: SlateContent) => void;
  restoreEditorBackup: (content: SlateContent) => void;
  isRecording: boolean;
  stopRecording: () => void;
  refetchCurrentCase?: () => Promise<unknown>;
  incrementReportVersion: () => Promise<Nullable<number>>;
}

/**
 * Owns report autosave/draft-save: the `reporterSavingState` atom, the debounced report save, and
 * the underlying update-report mutation flow. Receives its live dependencies (does not re-instantiate
 * stateful hooks the provider already owns) and returns referentially stable callbacks so the
 * provider's dependent effects/callbacks behave identically to before extraction.
 */
export const useAutoSaveReport = ({
  worklistItem,
  isReportFacadeEnabled,
  updateReport,
  updateAddendum,
  isClaimedByMe,
  workstationID,
  picklists,
  insertedMacros,
  enqueueToast,
  closeToast,
  maybeAutoloadNextCaseCallback,
  isWorklistAutoLoadEnabled,
  setDraftReportWorklistSmid,
  setHasReportContentChanged,
  setEditorBackup,
  restoreEditorBackup,
  isRecording,
  stopRecording,
  refetchCurrentCase,
  incrementReportVersion,
}: UseAutoSaveReportParams) => {
  const setSavingState = useSetRecoilState(reporterSavingState);

  const sendFieldsUpdate = useCallback(
    async ({
      activateTemplateIDToSubmit,
      isExplicitDraftSave,
      workstationIDToSubmit,
      errorMode = 'default',
      onSuccess,
      onError,
      content,
      updatedVersion,
      updateType,
    }: {
      activateTemplateIDToSubmit?: string;
      isExplicitDraftSave?: boolean;
      workstationIDToSubmit?: string | null;
      errorMode?: 'default' | 'submissionWorkstationPatch';
      onSuccess?: () => void;
      onError?: (error: ApolloError) => void;
      content: SlateContent;
      updatedVersion: Nullable<number>;
      updateType: Nullable<UpdateType>;
    }) => {
      // Only send picklists found on the current content
      const contentPicklistNodeIds = getPicklistsNodesFromParentNode({
        children: content,
      }).map(({ picklistID }) => picklistID);
      const filteredPicklists = picklists
        .filter(({ id }) => contentPicklistNodeIds.includes(id))
        .map(({ __typename, ...picklist }) => ({
          ...picklist,
          options: picklist.options.map(({ __typename, ...option }) => ({
            ...option,
            default: option.default != null ? option.default : false,
          })),
        }));

      /**
       * Helper to check if content has any anchor metadata
       * Anchors are only added by Report Facade operations
       */
      const hasAnchors = (sections: SlateContent): boolean => {
        const check = (node: any): boolean => {
          if (node == null || typeof node !== 'object') return false;
          if (node.data?.anchor) return true;
          if (Array.isArray(node)) {
            return node.some(check);
          }
          if (Array.isArray(node.children)) {
            return node.children.some(check);
          }
          return false;
        };
        return check(sections);
      };

      /**
       * Removes anchor metadata from Slate content before persistence.
       * Anchors are internal to Report Facade and shouldn't be persisted to the backend.
       */
      const sanitizeForPersistence = (sections: SlateContent): SlateContent => {
        let removedAnchors = 0;
        const walk = (node: any): any => {
          if (node == null || typeof node !== 'object') return node;
          const cloned: any = Array.isArray(node) ? [] : { ...node };
          if (Array.isArray(node)) {
            for (const child of node) cloned.push(walk(child));
            return cloned;
          }
          if (cloned.data && typeof cloned.data === 'object') {
            if ('anchor' in cloned.data) {
              const { anchor, ...rest } = cloned.data as any;
              removedAnchors++;
              cloned.data = rest;
            }
            if (cloned.data && Object.keys(cloned.data).length === 0) {
              delete cloned.data;
            }
          }
          if (Array.isArray(cloned.children)) {
            cloned.children = cloned.children.map(walk);
          }
          return cloned;
        };
        const sanitized = walk(sections) as SlateContent;
        if (removedAnchors > 0) {
          logger.info('[useAutoSaveReport] Sanitized sections: removed anchor metadata', {
            removedAnchors,
          });
        }
        return sanitized;
      };

      // Only sanitize if Report Facade is enabled AND content has anchors
      // This avoids performance overhead for existing functionality that doesn't use the facade
      const sanitizedSections =
        isReportFacadeEnabled && hasAnchors(content) ? sanitizeForPersistence(content) : content;

      const workstationIDForSubmission = workstationIDToSubmit ?? workstationID;
      let didSucceed: boolean = false;

      await updateReport({
        variables: {
          smid: getReportSMID(worklistItem),
          worklistItemSmid: worklistItem.smid,
          payload: {
            sections: sanitizedSections,
            templateId: activateTemplateIDToSubmit,
            picklists: filteredPicklists,
            workstationID: workstationIDForSubmission,
            version: updatedVersion,
            macroIds: insertedMacros.map((macro) => macro.smid),
          },
        },
        onCompleted: async () => {
          didSucceed = true;
          if (isExplicitDraftSave) {
            closeToast(`update-report-error-${getReportSMID(worklistItem)}`);
            closeToast(`save-draft-error-${getReportSMID(worklistItem)}`);

            const defaultToastMsg = `Draft report for ${getCaseInfo(worklistItem)} has been saved.`;

            await maybeAutoloadNextCaseCallback(isWorklistAutoLoadEnabled, defaultToastMsg, {
              reportExitAction: ReportExitAction.Draft,
              defaultToastMsg,
              isAddendum: false,
            });

            setDraftReportWorklistSmid(worklistItem.smid);
            setHasReportContentChanged(false);

            onSuccess && onSuccess();
          } else {
            closeToast(`update-report-error-${getReportSMID(worklistItem)}`);
            setEditorBackup(convertFieldsToUnified(content));
            setHasReportContentChanged(updateType === 'contentChange');
          }
          setSavingState(ReporterSaveState.Saved);
        },
        onError: (e) => {
          const doesNotHavePermission = e.message.includes(
            'You do not have permission to perform this action'
          );
          if (errorMode === 'submissionWorkstationPatch') {
            // Submission-time workstation patching provides its own retryable messaging.
          } else if (isExplicitDraftSave) {
            const reason = getSaveErrorReason(e);
            enqueueToast(
              createDraftSaveErrorMessage(
                `Unable to save the draft report for ${getDraftCaseInfo(worklistItem)} due to ${reason}.`
              ),
              {
                toastKey: `save-draft-error-${getReportSMID(worklistItem)}`,
                autoHideDuration: null,
                yOffset: 45,
                icon: 'redWarning',
              }
            );
          } else if (doesNotHavePermission) {
            if (isRecording) {
              stopRecording();
            }
            refetchCurrentCase && refetchCurrentCase();
            enqueueToast(
              'Unable to edit this report — it may have been claimed by another user or a clinic administrator. Refresh to see the current status.',
              {
                toastKey: `update-report-error-${getReportSMID(worklistItem)}`,
                autoHideDuration: null,
                yOffset: 45,
                icon: 'redWarning',
                isUnique: true,
              }
            );
          } else {
            enqueueAutoSaveErrorToast({
              enqueueToast,
              toastKey: `update-report-error-${getReportSMID(worklistItem)}`,
              reason: getSaveErrorReason(e),
            });
          }

          if (e.networkError == null) {
            restoreEditorBackup(convertFieldsToUnified(content));
          }

          if (doesNotHavePermission) {
            logger.warn(
              '[useAutoSaveReport] User does not have permission to update report',
              {
                reportSmid: getReportSMID(worklistItem),
                content,
                isExplicitDraftSave,
              },
              e
            );
          } else {
            logger.error(
              '[useAutoSaveReport] Error occurred when updating report',
              {
                reportSmid: getReportSMID(worklistItem),
                content,
                isExplicitDraftSave,
              },
              e
            );
          }

          setSavingState(ReporterSaveState.Error);
          setHasReportContentChanged(updateType === 'contentChange');

          onError && onError(e);
        },
      });

      return didSucceed;
    },
    [
      picklists,
      isReportFacadeEnabled,
      updateReport,
      worklistItem,
      workstationID,
      insertedMacros,
      setSavingState,
      closeToast,
      maybeAutoloadNextCaseCallback,
      isWorklistAutoLoadEnabled,
      setDraftReportWorklistSmid,
      setHasReportContentChanged,
      setEditorBackup,
      enqueueToast,
      isRecording,
      refetchCurrentCase,
      stopRecording,
      restoreEditorBackup,
    ]
  );

  const fieldsUpdate = useCallback(
    async ({
      contentToSubmit,
      updateType = null,
      activateTemplateIDToSubmit,
      isExplicitDraftSave = false,
      workstationIDToSubmit,
      errorMode = 'default',
      onSuccess,
      onError,
    }: FieldsUpdateProps) => {
      let content = contentToSubmit;
      if (content == null) {
        return;
      }

      content = convertUnifiedToFields(content);

      // Do not save the draft until its valid
      const emptyFieldCount = content.filter(
        (field: Node) => Element.isElement(field) && field.children.length === 0
      ).length;
      if (emptyFieldCount > 0) {
        logger.error(
          '[useAutoSaveReport] Draft report has invalid field content (empty field children). Not saving.',
          {
            reportSmid: getReportSMID(worklistItem),
            emptyFieldCount,
            fieldsContent: contentToSubmit,
            unifiedContent: content,
          }
        );

        setSavingState(ReporterSaveState.Error);

        return;
      }

      // We need to ensure that the next version is correct before sending the update
      const updatedVersion = await incrementReportVersion();

      logger.info('[useAutoSaveReport] Updating report...');

      return sendFieldsUpdate({
        activateTemplateIDToSubmit,
        isExplicitDraftSave,
        workstationIDToSubmit,
        errorMode,
        onSuccess,
        onError,
        content,
        updatedVersion,
        updateType,
      });
    },
    [setSavingState, worklistItem, incrementReportVersion, sendFieldsUpdate]
  );

  const fieldsUpdateDebounced = useDebouncedCallback(
    async ({ contentToSubmit, activateTemplateIDToSubmit, updateType }: FieldsUpdateProps) =>
      // autosave happens here
      fieldsUpdate({
        contentToSubmit,
        activateTemplateIDToSubmit,
        updateType,
      }),
    AUTO_SAVE_DEBOUNCE_MS,
    {
      trailing: true,
      leading: true,
      maxWait: AUTO_SAVE_MAX_WAIT_MS,
    }
  );

  const addendumUpdate = useCallback(
    async ({
      contentToSubmit,
      addendumSMID,
      isExplicitDraftSave = false,
      workstationIDToSubmit,
      errorMode = 'default',
      onSuccess,
      onError,
    }: {
      contentToSubmit: SlateContent;
      addendumSMID: string;
      isExplicitDraftSave?: boolean;
      workstationIDToSubmit?: string | null;
      errorMode?: 'default' | 'submissionWorkstationPatch';
      onSuccess?: () => void;
      onError?: (error: ApolloError) => void;
    }): Promise<boolean> => {
      if (contentToSubmit == null || addendumSMID == null || isClaimedByMe === false) return false;

      const workstationIDForSubmission = workstationIDToSubmit ?? workstationID;
      const sanitizedContentToSubmit = sanitizeAddendumContentForPersistence(contentToSubmit);
      let didSucceed: boolean = false;

      await updateAddendum({
        variables: {
          smid: addendumSMID,
          payload: { content: sanitizedContentToSubmit, workstationID: workstationIDForSubmission },
        },
        onCompleted: async () => {
          didSucceed = true;
          setSavingState(ReporterSaveState.Saved);

          if (isExplicitDraftSave) {
            closeToast(`save-draft-addendum-error-${getReportSMID(worklistItem)}`);
            const baseToastMsg = `Draft addendum for ${getCaseInfo(worklistItem)} has been saved.`;

            await maybeAutoloadNextCaseCallback(isWorklistAutoLoadEnabled, baseToastMsg, {
              reportExitAction: ReportExitAction.Draft,
              defaultToastMsg: baseToastMsg,
              isAddendum: true,
            });

            setDraftReportWorklistSmid(worklistItem.smid);
            setHasReportContentChanged(false);
            onSuccess && onSuccess();
          } else {
            closeToast(`update-report-error-${getReportSMID(worklistItem)}`);
            setEditorBackup(contentToSubmit);
          }
        },
        onError: (e) => {
          if (errorMode === 'submissionWorkstationPatch') {
            // Submission-time workstation patching provides its own retryable messaging.
          } else if (isExplicitDraftSave) {
            const reason = getSaveErrorReason(e);
            enqueueToast(
              createDraftSaveErrorMessage(
                `Unable to save the draft addendum for ${getDraftCaseInfo(worklistItem)} due to ${reason}.`
              ),
              {
                toastKey: `save-draft-addendum-error-${getReportSMID(worklistItem)}`,
                autoHideDuration: null,
                yOffset: 45,
                icon: 'redWarning',
              }
            );
          } else {
            enqueueAutoSaveErrorToast({
              enqueueToast,
              toastKey: `update-addendum-error-${getReportSMID(worklistItem)}`,
              reason: getSaveErrorReason(e),
            });
          }

          if (e.networkError == null) {
            restoreEditorBackup(contentToSubmit);
          }

          logger.error(
            '[useAutoSaveReport] Error occurred when updating addendum',
            {
              addendumSmid: addendumSMID,
              content: contentToSubmit,
            },
            e
          );

          setSavingState(ReporterSaveState.Error);

          onError && onError(e);
        },
      });

      return didSucceed;
    },
    [
      isClaimedByMe,
      updateAddendum,
      workstationID,
      setSavingState,
      closeToast,
      worklistItem,
      maybeAutoloadNextCaseCallback,
      isWorklistAutoLoadEnabled,
      setDraftReportWorklistSmid,
      setHasReportContentChanged,
      setEditorBackup,
      enqueueToast,
      restoreEditorBackup,
    ]
  );

  const addendumUpdateDebounced = useDebouncedCallback(addendumUpdate, AUTO_SAVE_DEBOUNCE_MS, {
    trailing: true,
    leading: false,
    maxWait: AUTO_SAVE_MAX_WAIT_MS,
  });

  return {
    setSavingState,
    fieldsUpdate,
    fieldsUpdateDebounced,
    addendumUpdate,
    addendumUpdateDebounced,
  };
};
